import type { ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { toParagraph } from '../utils/quinn';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { feeSchedule, fmtCents, moneyRange, type FeeLine } from '../utils/fees';
import { feeHead, feeRow, rowAmt, rowApplied, rowFreq, rowMain, rowMeta, rowName, rowNote } from '../utils/feeRows';

/* Situational fees are Quinn lease terms, not property data: theme defaults an editor can change or clear. */
const sit1Def: SlotGroupDefinition = {
  label: 'Situational Fee 1',
  description: 'Clear the name to hide this row.',
  fields: {
    name: { label: 'Fee', type: 'text', defaultValue: 'Early Termination Fee' },
    amount: { label: 'Amount', type: 'text', defaultValue: '200% of rent' },
    applied: { label: 'Applied', type: 'text', defaultValue: 'Per occurrence' },
  },
};
const sit2Def: SlotGroupDefinition = {
  label: 'Situational Fee 2',
  description: 'Clear the name to hide this row.',
  fields: {
    name: { label: 'Fee', type: 'text', defaultValue: 'Late Payment' },
    amount: { label: 'Amount', type: 'text', defaultValue: '10% of rent' },
    applied: { label: 'Applied', type: 'text', defaultValue: 'Per occurrence' },
  },
};
const sit3Def: SlotGroupDefinition = {
  label: 'Situational Fee 3',
  description: 'Clear the name to hide this row.',
  fields: {
    name: { label: 'Fee', type: 'text', defaultValue: 'Holdover Fee' },
    amount: { label: 'Amount', type: 'text', defaultValue: '$300' },
    applied: { label: 'Applied', type: 'text', defaultValue: 'Per occurrence' },
  },
};
const sit4Def: SlotGroupDefinition = {
  label: 'Situational Fee 4',
  description: 'Clear the name to hide this row.',
  fields: {
    name: { label: 'Fee', type: 'text', defaultValue: 'Returned Payment Fee (NSF)' },
    amount: { label: 'Amount', type: 'text', defaultValue: '$35' },
    applied: { label: 'Applied', type: 'text', defaultValue: 'Per occurrence' },
  },
};
const sit5Def: SlotGroupDefinition = {
  label: 'Situational Fee 5',
  description: 'Clear the name to hide this row.',
  fields: {
    name: { label: 'Fee', type: 'text', defaultValue: 'Legal / Eviction Fees' },
    amount: { label: 'Amount', type: 'text', defaultValue: 'Varies' },
    applied: { label: 'Applied', type: 'text', defaultValue: 'Per occurrence' },
  },
};
const sit6Def: SlotGroupDefinition = {
  label: 'Situational Fee 6',
  description: 'Clear the name to hide this row.',
  fields: {
    name: { label: 'Fee', type: 'text', defaultValue: 'Transfer Fee' },
    amount: { label: 'Amount', type: 'text', defaultValue: '$1,500' },
    applied: { label: 'Applied', type: 'text', defaultValue: 'Per occurrence' },
  },
};

const groupHead = 'flex flex-col gap-1.5 pb-1 pt-10 max-[767px]:pt-7';
const groupTitle = 'm-0 font-heading text-[28px] font-normal leading-[34px] [&_p]:m-0 max-[767px]:text-[24px] max-[767px]:leading-[30px]';
const groupLead = 'm-0 max-w-[800px] text-sm leading-5 text-[--q-muted] [&_p]:m-0';

/** The full fee schedule, grouped as Quinn's printed guide: move-in, monthly essentials, add-ons, situational. */
export function FeeTable() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const s = feeSchedule(p);
  const sits = [
    useSlotGroup('feetable.situational_1', sit1Def),
    useSlotGroup('feetable.situational_2', sit2Def),
    useSlotGroup('feetable.situational_3', sit3Def),
    useSlotGroup('feetable.situational_4', sit4Def),
    useSlotGroup('feetable.situational_5', sit5Def),
    useSlotGroup('feetable.situational_6', sit6Def),
  ];
  const monthly = moneyRange(s.monthlyLo, s.monthlyHi);

  if (!s.moveIn.length && !s.monthly.length && !editing) return null;

  const row = (l: FeeLine, i: number, extra?: ReactNode) => (
    <div className={feeRow} key={`${l.name}-${i}`}>
      <div className={rowMain}>
        <span className={rowName}>{l.name}</span>
        <span className={rowAmt}>{l.amount}</span>
        <span className={rowApplied}>{l.applied}</span>
        <span className={rowFreq}>{l.freq}</span>
        <span className={rowMeta}>{[l.applied, l.freq].filter(Boolean).join(' · ')}</span>
      </div>
      {l.note && <p className={rowNote}>{l.note}</p>}
      {!l.note && extra}
    </div>
  );
  const sitRow = (v: Record<string, string | null>) =>
    v.name ? (
      <div className={feeRow}>
        <div className={rowMain}>
          <span className={rowName}>{v.name}</span>
          <span className={rowAmt}>{v.amount}</span>
          <span className={rowApplied}>{v.applied}</span>
          <span className={rowFreq} />
          <span className={rowMeta}>{v.applied}</span>
        </div>
      </div>
    ) : editing ? (
      <div className={`${feeRow} text-sm text-[--q-muted] [border-bottom-style:dashed]`}>Hidden row</div>
    ) : null;

  return (
    <section className="px-[--gutter] pb-[72px] pt-2 max-[767px]:pb-8 max-[767px]:pt-0">
      <div className={feeHead} aria-hidden="true">
        <span>Fee</span>
        <span className="text-right">Amount</span>
        <span className="pl-10 max-[1199px]:pl-6">Applied</span>
        <span>Frequency</span>
      </div>

      {s.moveIn.length > 0 && (
        <div>
          <div className={groupHead}>
            <RichTextContent path="feetable.movein_title" as="h2" className={groupTitle} />
            <RichTextContent
              path="feetable.movein_lead"
              as="p"
              className={groupLead}
              defaultValue={toParagraph(s.moveInTotal != null ? `Known charges for one adult are ${fmtCents(s.moveInTotal)}, before the deposit.` : undefined)}
            />
          </div>
          {s.moveIn.map((l, i) => row(l, i))}
        </div>
      )}

      {s.monthly.length > 0 && (
        <div>
          <div className={groupHead}>
            <RichTextContent path="feetable.monthly_title" as="h2" className={groupTitle} />
            <RichTextContent path="feetable.monthly_lead" as="p" className={groupLead} defaultValue={toParagraph(monthly ? `${monthly} before rent.` : undefined)} />
          </div>
          {s.monthly.map((l, i) =>
            row(l, i, /concierge/i.test(l.name) ? <RichTextContent path="feetable.concierge_note" as="p" className={rowNote} /> : undefined),
          )}
        </div>
      )}

      {s.addOns.length > 0 && (
        <div>
          <div className={groupHead}>
            <RichTextContent path="feetable.addons_title" as="h2" className={groupTitle} />
            <RichTextContent path="feetable.addons_lead" as="p" className={groupLead} />
          </div>
          {s.addOns.map((l, i) => row(l, i))}
        </div>
      )}

      <div>
        <div className={groupHead}>
          <RichTextContent path="feetable.situational_title" as="h2" className={groupTitle} />
          <RichTextContent path="feetable.situational_lead" as="p" className={groupLead} />
        </div>
        <EditableSlotGroup groupPath="feetable.situational_1" definition={sit1Def}>{sitRow(sits[0])}</EditableSlotGroup>
        <EditableSlotGroup groupPath="feetable.situational_2" definition={sit2Def}>{sitRow(sits[1])}</EditableSlotGroup>
        <EditableSlotGroup groupPath="feetable.situational_3" definition={sit3Def}>{sitRow(sits[2])}</EditableSlotGroup>
        <EditableSlotGroup groupPath="feetable.situational_4" definition={sit4Def}>{sitRow(sits[3])}</EditableSlotGroup>
        <EditableSlotGroup groupPath="feetable.situational_5" definition={sit5Def}>{sitRow(sits[4])}</EditableSlotGroup>
        <EditableSlotGroup groupPath="feetable.situational_6" definition={sit6Def}>{sitRow(sits[5])}</EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeeTable',
  displayName: 'Fee Table',
  tokens: {
    'feetable.movein_title': { label: 'Move-in · Title', type: 'richtext', defaultValue: '<p>Required move-in fees</p>' },
    'feetable.movein_lead': {
      label: 'Move-in · Lead',
      description: 'Until edited, gives the known move-in charges for one adult.',
      type: 'richtext',
      defaultValue: '<p>Known charges before the deposit.</p>',
    },
    'feetable.monthly_title': { label: 'Monthly · Title', type: 'richtext', defaultValue: '<p>Required monthly essentials</p>' },
    'feetable.monthly_lead': {
      label: 'Monthly · Lead',
      description: 'Until edited, gives the total of the required monthly charges.',
      type: 'richtext',
      defaultValue: '<p>Required every month, before rent.</p>',
    },
    'feetable.concierge_note': {
      label: 'Concierge · What It Includes',
      description: 'Shown under the concierge fee.',
      type: 'richtext',
      defaultValue: '<p>Includes bi-annual filter change, exterior pest control, 24 hour maintenance response on emergencies, and positive credit reporting.</p>',
      generator: {
        prompt: "One sentence listing what this property's concierge package includes, taken from its FAQ answer about the concierge package; leave empty if the property does not say.",
        context: 'Note under the Concierge Fee row in the Required monthly essentials group of the Easy Fee Guide table.',
        length: '1 sentence',
      },
    },
    'feetable.addons_title': { label: 'Add-ons · Title', type: 'richtext', defaultValue: '<p>Personalized add-ons</p>' },
    'feetable.addons_lead': { label: 'Add-ons · Lead', type: 'richtext', defaultValue: '<p>Only if they apply to you.</p>' },
    'feetable.situational_title': { label: 'Situational · Title', type: 'richtext', defaultValue: '<p>Situational fees</p>' },
    'feetable.situational_lead': { label: 'Situational · Lead', type: 'richtext', defaultValue: '<p>Only in specific circumstances.</p>' },
  },
  slotGroups: {
    'feetable.situational_1': sit1Def,
    'feetable.situational_2': sit2Def,
    'feetable.situational_3': sit3Def,
    'feetable.situational_4': sit4Def,
    'feetable.situational_5': sit5Def,
    'feetable.situational_6': sit6Def,
  },
};

(FeeTable as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FeeTable;
